import { trapFocus, removeTrapFocus } from '@theme/focus';
import { DialogOpenEvent, DialogCloseEvent } from '@theme/dialog';

const instances = new WeakMap();

function prefersReducedMotion() {
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

function initReviewsCarousel(root) {
  if (instances.has(root)) return;

  const track = root.querySelector('[data-reviews-track]');
  const viewport = root.querySelector('[data-reviews-viewport]');
  const controls = root.querySelector('[data-reviews-controls]');
  const dots = root.querySelector('[data-reviews-dots]');
  const pageCount = root.querySelector('[data-reviews-page-count]');
  const previous = root.querySelector('[data-reviews-previous]');
  const next = root.querySelector('[data-reviews-next]');
  const dialogComponent = root.querySelector('.reviews-carousel__dialog-component');
  const dialog = dialogComponent?.querySelector('[ref="dialog"]');
  const dialogBody = root.querySelector('[data-reviews-dialog-body]');

  if (!(track instanceof HTMLElement) || !(viewport instanceof HTMLElement)) return;

  let activePage = 0;
  let columns = 1;
  let settleTimer;
  let layoutFrame;
  let destroyed = false;
  let previousFocus = null;
  let focusTrapOwned = false;
  const showArrows = root.dataset.showArrows === 'true';
  const showDots = root.dataset.showDots === 'true';
  const editorMode = root.dataset.designMode === 'true' || document.body.classList.contains('shopify-design-mode');

  const getCards = () =>
    [...track.querySelectorAll(':scope > [data-review-slide]')].filter(
      (card) => !card.hidden && (editorMode || card.dataset.reviewEmpty !== 'true')
    );

  const applyContentLayout = (card) => {
    const content = card.querySelector('[data-review-card-content]');
    if (!(content instanceof HTMLElement)) return;

    const fields = [...content.children].filter(
      (field) => field instanceof HTMLElement && field.matches('[data-review-field]')
    );
    if (!fields.length) return;

    fields.forEach((field) => {
      field.style.removeProperty('grid-row');
      field.style.removeProperty('grid-column');
    });

    const rows = [];
    for (let index = 0; index < fields.length; index += 1) {
      const field = fields[index];
      const nextField = fields[index + 1];
      const isMetadataPair =
        nextField &&
        ((['name', 'date'].includes(field.dataset.reviewField) &&
          ['name', 'date'].includes(nextField.dataset.reviewField) &&
          field.dataset.reviewField !== nextField.dataset.reviewField) ||
          (field.dataset.reviewField === 'name' && nextField.dataset.reviewField === 'source'));

      if (isMetadataPair) {
        rows.push([field, nextField]);
        index += 1;
      } else {
        rows.push([field]);
      }
    }

    content.style.gridTemplateRows = rows
      .map((row) => (row.some((field) => field.dataset.reviewField === 'description') ? 'minmax(0, 1fr)' : 'auto'))
      .join(' ');

    rows.forEach((row, rowIndex) => {
      row.forEach((field, columnIndex) => {
        field.style.gridRow = `${rowIndex + 1}`;
        field.style.gridColumn = row.length > 1 ? `${columnIndex + 1}` : '1 / -1';
      });
    });
  };

  const getColumns = () => {
    const value = Number.parseInt(getComputedStyle(track).getPropertyValue('--reviews-columns-current'), 10);
    return Number.isFinite(value) && value > 0 ? value : 1;
  };

  const totalPages = () => Math.max(1, Math.ceil(getCards().length / columns));

  const pageTarget = (page) => {
    const cards = getCards();
    return cards[Math.min(page * columns, Math.max(0, cards.length - 1))];
  };

  const pageOffset = (page) => {
    const target = pageTarget(page);
    const first = getCards()[0];
    if (!(target instanceof HTMLElement) || !(first instanceof HTMLElement)) return 0;
    return Math.max(0, target.offsetLeft - first.offsetLeft);
  };

  const labelForPage = (page) => {
    const label = root.dataset.pageLabel || '';
    return label ? label.replace('[page]', `${page + 1}`) : `${page + 1}`;
  };

  const updateControls = () => {
    const pages = totalPages();
    activePage = Math.max(0, Math.min(activePage, pages - 1));
    if (controls instanceof HTMLElement) controls.hidden = pages <= 1;

    if (pageCount) {
      pageCount.textContent = `${activePage + 1} / ${pages}`;
      pageCount.setAttribute('aria-label', labelForPage(activePage));
    }

    if (dots) {
      [...dots.children].forEach((dot, index) => {
        const selected = index === activePage;
        dot.setAttribute('aria-current', selected ? 'page' : 'false');
        dot.setAttribute('aria-label', labelForPage(index));
      });
    }

    if (previous instanceof HTMLButtonElement) previous.disabled = activePage === 0;
    if (next instanceof HTMLButtonElement) next.disabled = activePage === pages - 1;

    getCards().forEach((card, index) => {
      card.setAttribute('aria-hidden', `${Math.floor(index / columns) !== activePage}`);
      card.inert = Math.floor(index / columns) !== activePage;
    });
  };

  const reconcileDots = () => {
    if (!(dots instanceof HTMLElement)) return;
    const pages = totalPages();

    while (dots.children.length > pages) dots.lastElementChild?.remove();
    while (dots.children.length < pages) {
      const index = dots.children.length;
      const dot = document.createElement('button');
      dot.type = 'button';
      dot.className = 'reviews-carousel__dot';
      dot.addEventListener('click', () => goToPage(index));
      dots.append(dot);
    }
  };

  const measureOverflow = () => {
    /** @type {{ description: HTMLElement, trigger: HTMLButtonElement }[]} */
    const descriptions = [];
    for (const card of getCards()) {
      const description = card.querySelector('[data-review-field="description"]');
      const trigger = card.querySelector('[data-review-more]');
      if (description instanceof HTMLElement && trigger instanceof HTMLButtonElement) {
        descriptions.push({ description, trigger });
      }
    }

    for (const { description, trigger } of descriptions) {
      description.style.removeProperty('max-height');
      description.dataset.reviewOverflow = 'false';
      trigger.hidden = true;
    }

    const measurements = descriptions.map(({ description, trigger }) => ({
      description,
      trigger,
      overflow: description.scrollHeight > description.clientHeight + 1,
    }));

    for (const { description, trigger, overflow } of measurements) {
      if (!overflow) continue;
      description.dataset.reviewOverflow = 'true';
      trigger.hidden = false;
    }
  };

  function goToPage(page, animate = true) {
    const pages = totalPages();
    activePage = Math.max(0, Math.min(Number(page) || 0, pages - 1));
    const left = pageOffset(activePage);
    updateControls();
    track.scrollTo({
      left,
      behavior: animate && !prefersReducedMotion() ? 'smooth' : 'instant',
    });
  }

  const settleScroll = () => {
    const cards = getCards();
    if (!cards.length) return;
    const currentLeft = track.scrollLeft;
    let closestPage = 0;
    let closestDistance = Number.POSITIVE_INFINITY;

    for (let page = 0; page < totalPages(); page += 1) {
      const distance = Math.abs(pageOffset(page) - currentLeft);
      if (distance < closestDistance) {
        closestDistance = distance;
        closestPage = page;
      }
    }

    if (closestPage !== activePage) {
      activePage = closestPage;
      updateControls();
    }
  };

  const reconcileTrailingSpace = () => {
    track.querySelectorAll(':scope > [data-reviews-spacer]').forEach((spacer) => spacer.remove());
    const missing = (columns - (getCards().length % columns)) % columns;
    for (let index = 0; index < missing; index += 1) {
      const spacer = document.createElement('span');
      spacer.className = 'reviews-carousel__spacer';
      spacer.setAttribute('data-reviews-spacer', '');
      spacer.setAttribute('aria-hidden', 'true');
      track.append(spacer);
    }
  };

  const updateLayout = () => {
    if (destroyed || !root.isConnected) return;
    const firstVisibleIndex = activePage * columns;
    columns = getColumns();
    const cards = getCards();
    activePage = cards.length ? Math.min(Math.floor(firstVisibleIndex / columns), totalPages() - 1) : 0;
    reconcileTrailingSpace();

    cards.forEach(applyContentLayout);

    cards.forEach((card, index) => {
      if (index % columns === 0) card.setAttribute('data-page-start', '');
      else card.removeAttribute('data-page-start');
    });

    root.dataset.reviewsReady = 'true';
    reconcileDots();
    measureOverflow();
    goToPage(activePage, false);
  };

  const queueLayout = () => {
    if (destroyed || !root.isConnected) return;
    cancelAnimationFrame(layoutFrame);
    layoutFrame = requestAnimationFrame(updateLayout);
  };

  const handleKeydown = (event) => {
    if (event.altKey || event.ctrlKey || event.metaKey) return;
    if (event.target instanceof Element && event.target.closest('input, textarea, select, [contenteditable="true"]')) return;
    if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
    event.preventDefault();
    const delta = event.key === 'ArrowRight' ? 1 : event.key === 'ArrowLeft' ? -1 : 0;
    goToPage(event.key === 'Home' ? 0 : event.key === 'End' ? totalPages() - 1 : activePage + delta);
  };

  const handleScroll = () => {
    window.clearTimeout(settleTimer);
    settleTimer = window.setTimeout(settleScroll, 140);
  };

  const cleanClone = (clone) => {
    const cleanElement = (element) => {
      [...element.attributes].forEach((attribute) => {
        if (attribute.name === 'id' || attribute.name.startsWith('data-shopify-editor')) {
          element.removeAttribute(attribute.name);
        }
      });
      element.removeAttribute('inert');
    };

    cleanElement(clone);
    clone.removeAttribute('aria-hidden');
    clone.querySelectorAll('*').forEach(cleanElement);
    clone.querySelectorAll('[data-review-overflow]').forEach((element) => {
      element.removeAttribute('data-review-overflow');
      element.style.removeProperty('max-height');
    });
    if (clone.matches('[data-review-card-content]')) {
      clone.style.removeProperty('grid-template-rows');
      clone.querySelectorAll('[data-review-field]').forEach((field) => {
        field.style.removeProperty('grid-row');
        field.style.removeProperty('grid-column');
      });
    }
    clone.removeAttribute('id');
    return clone;
  };

  const openReview = (event) => {
    const trigger = event.currentTarget;
    const card = trigger.closest('[data-review-slide]');
    if (!(trigger instanceof HTMLElement) || !(card instanceof HTMLElement) || !(dialogBody instanceof HTMLElement)) return;

    const content = card.querySelector('[data-review-card-content]');
    if (!(content instanceof HTMLElement)) return;

    previousFocus = trigger;
    const clone = cleanClone(content.cloneNode(true));
    dialogBody.replaceChildren(clone);
    dialogComponent?.showDialog?.();
  };

  const handleDialogOpen = () => {
    if (!(dialog instanceof HTMLDialogElement)) return;
    trapFocus(dialog);
    focusTrapOwned = true;
    const closeButton = dialog.querySelector('.reviews-carousel__dialog-close');
    if (closeButton instanceof HTMLElement) closeButton.focus({ preventScroll: true });
  };

  const handleDialogClose = () => {
    if (focusTrapOwned) removeTrapFocus();
    focusTrapOwned = false;
    if (previousFocus instanceof HTMLElement && previousFocus.isConnected) {
      const slide = previousFocus.closest('[data-review-slide]');
      const cards = getCards();
      const index = slide ? cards.indexOf(slide) : -1;
      if (index >= 0 && slide?.matches('[aria-hidden="true"]')) goToPage(Math.floor(index / columns), false);
      previousFocus.focus({ preventScroll: true });
    }
    previousFocus = null;
  };

  const handleDialogKeydown = (event) => {
    if (event.key !== 'Tab' || !(dialog instanceof HTMLDialogElement) || !dialog.open) return;
    const focusable = [...dialog.querySelectorAll('button:enabled, a[href], [tabindex]:not([tabindex="-1"])')].filter(
      (element) => element instanceof HTMLElement && !element.hidden && element.getClientRects().length
    );
    if (!focusable.length) return;
    const current = focusable.indexOf(document.activeElement);
    const target = event.shiftKey
      ? focusable[(current <= 0 ? focusable.length : current) - 1]
      : focusable[(current + 1) % focusable.length];
    event.preventDefault();
    target?.focus();
  };

  const handleBlockSelect = (event) => {
    const target = event.target instanceof Element ? event.target : null;
    const slide = target?.closest('[data-review-slide]');
    if (!slide || !track.contains(slide)) return;
    const cards = getCards();
    const index = cards.indexOf(slide);
    if (index >= 0) goToPage(Math.floor(index / columns), false);
  };

  const handleSectionUnload = (event) => {
    if (!(event.target instanceof Element) || !event.target.contains(root)) return;
    destroy();
  };

  const resizeObserver = new ResizeObserver(queueLayout);
  resizeObserver.observe(track);
  const arrows = root.querySelector('[data-reviews-arrows]');
  if (arrows instanceof HTMLElement) arrows.hidden = !showArrows;
  if (dots instanceof HTMLElement) dots.hidden = !showDots;
  track.addEventListener('scroll', handleScroll, { passive: true });
  viewport.addEventListener('keydown', handleKeydown);
  const handlePrevious = () => goToPage(activePage - 1);
  const handleNext = () => goToPage(activePage + 1);
  previous?.addEventListener('click', handlePrevious);
  next?.addEventListener('click', handleNext);
  track.querySelectorAll('[data-review-more]').forEach((trigger) => trigger.addEventListener('click', openReview));
  dialogComponent?.addEventListener(DialogOpenEvent.eventName, handleDialogOpen);
  dialogComponent?.addEventListener(DialogCloseEvent.eventName, handleDialogClose);
  dialog?.addEventListener('keydown', handleDialogKeydown);
  document.addEventListener('shopify:block:select', handleBlockSelect);
  document.addEventListener('shopify:section:unload', handleSectionUnload);

  function destroy() {
    destroyed = true;
    if (dialog?.open) dialogComponent?.closeDialog?.();
    cancelAnimationFrame(layoutFrame);
    window.clearTimeout(settleTimer);
    resizeObserver.disconnect();
    track.removeEventListener('scroll', handleScroll);
    viewport.removeEventListener('keydown', handleKeydown);
    previous?.removeEventListener('click', handlePrevious);
    next?.removeEventListener('click', handleNext);
    track.querySelectorAll('[data-review-more]').forEach((trigger) => trigger.removeEventListener('click', openReview));
    dialogComponent?.removeEventListener(DialogOpenEvent.eventName, handleDialogOpen);
    dialogComponent?.removeEventListener(DialogCloseEvent.eventName, handleDialogClose);
    dialog?.removeEventListener('keydown', handleDialogKeydown);
    document.removeEventListener('shopify:block:select', handleBlockSelect);
    document.removeEventListener('shopify:section:unload', handleSectionUnload);
    if (focusTrapOwned) removeTrapFocus();
    focusTrapOwned = false;
    instances.delete(root);
  }

  instances.set(root, { destroy });
  document.fonts?.ready.then(() => {
    if (root.isConnected) queueLayout();
  });
  queueLayout();
}

function initAll() {
  document.querySelectorAll('[data-reviews-carousel]').forEach(initReviewsCarousel);
}

if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initAll, { once: true });
else initAll();

document.addEventListener('shopify:section:load', (event) => {
  if (event.target instanceof Element) event.target.querySelectorAll('[data-reviews-carousel]').forEach(initReviewsCarousel);
});
